// Fechas de licencia: UN SOLO CRITERIO para mostrarlas y contarlas.
//
// La fecha de una licencia (`licence.untildate`) NO es el último día con licencia: es
// el PRIMER DÍA SIN licencia. Lo decide el programa, que la lee como la medianoche de
// ese día y deja de funcionar ahí. Con fecha 09/11 se trabaja hasta el 08/11 a las
// 23:59 y el 09 ya está bloqueado.
//
// Antes el aviso de vencimiento y la pantalla de estacionamientos lo resolvían cada uno
// por su cuenta, y no coincidían: uno daba la licencia por vencida un día después que
// el otro, y los dos mostraban la fecha cruda con "vence el". Todo lo que muestre un
// vencimiento pasa por acá:
//
//   fechaUltimoDia      la fecha que se le dice al cliente: el último día
//   diasDeLicencia      días que le quedan DESPUÉS de hoy: 0 = hoy es el último día
//   licenciaVigente     ¿puede trabajar hoy?
//   textoLicencia       todo lo anterior dicho en una frase
//
// La misma convención está en TCADMIN (src/helpers/fechas.js), en el portal de
// licencias (src/helpers/fechasLicencia.js) y en las functions (fechasLicencia.js).

/** "2026-11-09T00:00:00.000Z" | "2026-11-09" -> "2026-11-09" */
export const isoAYMD = (v) => (v ? String(v).slice(0, 10) : "");

const partes = (v) => {
  const [y, m, d] = isoAYMD(v).split("-").map(Number);
  return y && m && d ? [y, m, d] : null;
};

const mostrar = (ymd) => {
  const [y, m, d] = String(ymd).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "";
};

/** Último día con licencia: "2026-11-09" -> "2026-11-08". Sin fecha legible, "". */
export const ultimoDiaLicencia = (v) => {
  const p = partes(v);
  return p ? new Date(Date.UTC(p[0], p[1] - 1, p[2] - 1)).toISOString().slice(0, 10) : "";
};

/** El último día con licencia, para mostrar: "2026-11-09" -> "08/11/2026". */
export const fechaUltimoDia = (v) => mostrar(ultimoDiaLicencia(v));

/**
 * Días de licencia que quedan después de hoy. 0 = hoy es el último día; negativo =
 * vencida desde hace esa cantidad de días. `null` sin fecha legible.
 * Cuenta días de calendario, no horas: el resultado no cambia a lo largo del día.
 */
export const diasDeLicencia = (v, hoy = new Date()) => {
  const p = partes(v);
  if (!p) return null;
  const fecha = Date.UTC(p[0], p[1] - 1, p[2], 12);
  const ref = Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate(), 12);
  return Math.round((fecha - ref) / 86400000) - 1;
};

/** ¿La licencia permite trabajar hoy? Sin fecha legible, false. */
export const licenciaVigente = (v, hoy = new Date()) => {
  const dias = diasDeLicencia(v, hoy);
  return dias !== null && dias >= 0;
};

// Desde cuántos días antes se destaca que la licencia está por vencer.
export const DIAS_POR_VENCER = 14;

/**
 * El estado de una licencia en una frase, para el aviso y la tarjeta del
 * estacionamiento. Devuelve también en qué caso está, para elegir color e ícono:
 *   "vencida" · "ultimo_dia" · "por_vencer" · "vigente"   (`null` sin fecha legible)
 */
export const textoLicencia = (v, hoy = new Date()) => {
  const dias = diasDeLicencia(v, hoy);
  if (dias === null) return null;
  const ultimoDia = fechaUltimoDia(v);
  if (dias < 0) {
    return { caso: "vencida", dias, texto: `Licencia vencida. Su último día fue el ${ultimoDia}` };
  }
  if (dias === 0) {
    return { caso: "ultimo_dia", dias, texto: "Hoy es el último día de la licencia" };
  }
  if (dias <= DIAS_POR_VENCER) {
    return {
      caso: "por_vencer",
      dias,
      texto: `Licencia hasta el ${ultimoDia} inclusive (${dias === 1 ? "queda 1 día" : `quedan ${dias} días`})`,
    };
  }
  return { caso: "vigente", dias, texto: `Licencia hasta el ${ultimoDia} inclusive` };
};
